import { useMemo } from "react"
import { roundNumber, quantityOf } from "../helpers/formatting"
import { roundMoney }  from "@react/helpers/helpers"

export const calcComponentTotal = (comp, viewMode = "sale", parentQty = 1) => {
  const qty = parseFloat(comp.quantity) || 0
  const price = viewMode === "cost"
    ? (parseFloat(comp.purchase_price) || 0)
    : (parseFloat(comp.unit_price) || 0)
  const discount = parseFloat(comp.discount) || 0
  const factor   = quantityOf(parentQty)
  return roundNumber(factor * qty * price * (1 - discount / 100))
}

export const calcGroupTotal = (group, viewMode = "sale") => {
  const components = (group.components || []).filter(c => !c._destroy && !c.option)
  const componentsSum = components.reduce((sum, comp) => sum + calcComponentTotal(comp, viewMode), 0)
  const coefficient = parseFloat(group.coefficient) || 1
  const quantity = parseFloat(group.quantity) || 1
  return roundNumber(componentsSum * coefficient * quantity)
}

export const useCalculations = (section, viewMode) => {
  return useMemo(() => {
    const children = (section.children || []).filter(c => !c._destroy)

    const groupTotals = {}
    children.forEach(child => {
      groupTotals[child._uid] = calcGroupTotal(child, viewMode)
    })

    const sectionTotal = Object.values(groupTotals).reduce((s, v) => s + v, 0)

    return { sectionTotal: roundNumber(sectionTotal), groupTotals }
  }, [section, viewMode])
}

export const useQuoteTotals = (sections, discount = 0, viewMode = "sale") => {
  return useMemo(() => {
    const activeSections = sections.filter(l => !l._destroy)

    let subtotalHT = 0
    let subtotalPurchase = 0
    const vatBreakdown = {}

    activeSections.forEach(section => {
      const groups = (section.children || []).filter(c => !c._destroy)
      groups.forEach(group => {
        const coefficient = parseFloat(group.coefficient) || 1
        const quantity = parseFloat(group.quantity) || 1
        const components = (group.components || []).filter(c => !c._destroy && !c.option)
        components.forEach(comp => {
          const saleTotal = calcComponentTotal(comp, "sale")
          const costTotal = calcComponentTotal(comp, "cost")
          const scaledSale = roundNumber(saleTotal * coefficient * quantity)
          const scaledCost = roundNumber(costTotal * coefficient * quantity)
          subtotalHT += scaledSale
          subtotalPurchase += scaledCost

          const vatPct = comp.vat_rate?.percentage
          if (vatPct) {
            vatBreakdown[vatPct] = (vatBreakdown[vatPct] || 0) + scaledSale
          }
        })
      })
    })

    subtotalHT = roundNumber(subtotalHT)
    subtotalPurchase = roundNumber(subtotalPurchase)
    const discountAmount = roundNumber(subtotalHT * discount / 100)
    const totalHT = roundNumber(subtotalHT - discountAmount)

    const vatAmounts = {}
    let totalVAT = 0
    Object.entries(vatBreakdown).forEach(([pct, base]) => {
      const discountedBase = roundMoney(base * (1 - discount / 100))
      const vatAmount = roundMoney(parseFloat(pct) * discountedBase / 100)
      vatAmounts[pct] = vatAmount
      totalVAT += vatAmount
    })
    totalVAT = roundNumber(totalVAT)

    const totalTTC = roundNumber(totalHT + totalVAT)
    const marginPct = subtotalHT > 0 ? roundNumber((subtotalHT - subtotalPurchase) / subtotalHT * 100) : 0
    const marginAmount = roundNumber(subtotalHT - subtotalPurchase)

    return {
      subtotalHT,
      subtotalPurchase,
      discountAmount,
      totalHT,
      vatAmounts,
      totalVAT,
      totalTTC,
      marginPct,
      marginAmount,
    }
  }, [sections, discount, viewMode])
}
